import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { T, Var, useGT } from 'gt-react';
import { PencilSimpleIcon } from '@phosphor-icons/react';
import type {
  PricelistCaseLogFieldsDTO,
  PricelistCaseResponse,
} from '@erp/rpc';
import { orpc } from '@/api/rpc/client';
import { useToast } from 'src/context/ToastContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Field, SectionLabel } from './pricelistDetailFields';

/** The three fields as text, '' for unset. */
interface LogFieldValues {
  vendorCode: string;
  nationalImComparingComments: string;
  uploadComments: string;
}

function valuesFrom(fields: PricelistCaseLogFieldsDTO): LogFieldValues {
  return {
    vendorCode: fields.vendorCode ?? '',
    nationalImComparingComments: fields.nationalImComparingComments ?? '',
    uploadComments: fields.uploadComments ?? '',
  };
}

function toPatch(values: LogFieldValues): PricelistCaseLogFieldsDTO {
  const blankToNull = (value: string) =>
    value.trim() === '' ? null : value.trim();
  return {
    vendorCode: blankToNull(values.vendorCode),
    nationalImComparingComments: blankToNull(
      values.nationalImComparingComments,
    ),
    uploadComments: blankToNull(values.uploadComments),
  };
}

function sameValues(a: LogFieldValues, b: LogFieldValues): boolean {
  return (
    a.vendorCode.trim() === b.vendorCode.trim() &&
    a.nationalImComparingComments.trim() ===
      b.nationalImComparingComments.trim() &&
    a.uploadComments.trim() === b.uploadComments.trim()
  );
}

/**
 * The price-file-log columns that exist only on the case: the "3 LETTER CODE
 * / PG" (defaulting to the vendor's own code, overridable for commodity files
 * that span many vendor numbers — GEN-2578) and the two internal-comment
 * columns the export used to leave blank.
 *
 * Its own card rather than more fields on the details card because the
 * details form is shared with the file page and the seeding preview, and
 * these three have no legacy counterpart to seed from. Same pencil / Cancel /
 * Save shape as the details card, same `cases.update` write, same audit
 * trigger recording the diff.
 */
export function PricelistCaseLogFieldsCard({
  pricelistCase,
}: {
  pricelistCase: PricelistCaseResponse;
}) {
  const gt = useGT();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [state, setState] = useState<{
    source: PricelistCaseLogFieldsDTO;
    values: LogFieldValues;
  }>(() => ({
    source: pricelistCase.logFields,
    values: valuesFrom(pricelistCase.logFields),
  }));
  // A fresh fetch replaces the form's copy so a save elsewhere is not
  // overwritten by stale inputs ("adjust state when props change").
  if (state.source !== pricelistCase.logFields) {
    setState({
      source: pricelistCase.logFields,
      values: valuesFrom(pricelistCase.logFields),
    });
  }
  const updateMutation = useMutation(
    orpc.inventoryManagement.pricelistCases.update.mutationOptions(),
  );

  function patch(values: Partial<LogFieldValues>) {
    setState((prev) => ({ ...prev, values: { ...prev.values, ...values } }));
  }

  const isDirty = !sameValues(state.values, valuesFrom(state.source));

  async function save() {
    try {
      await updateMutation.mutateAsync({
        caseId: pricelistCase.id,
        details: {},
        logFields: toPatch(state.values),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.inventoryManagement.pricelistCases.get.key(),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.inventoryManagement.pricelistCases.timeline.key(),
      });
      setIsEditing(false);
      showToast({
        title: gt('Saved', {
          context: 'toast title when the price file log fields are saved',
        }),
        message: gt('The price file log fields were updated.', {
          context: 'toast message when the price file log fields are saved',
        }),
        color: 'success',
      });
    } catch {
      showToast({
        title: gt('Error', { context: 'toast title for error state' }),
        message: gt('Failed to save the price file log fields.', {
          context: 'toast message when saving the price file log fields fails',
        }),
        color: 'error',
      });
    }
  }

  const effectiveCode =
    state.source.vendorCode ?? pricelistCase.companyCode ?? null;

  return (
    <div className="flex flex-col gap-4 rounded-md border bg-card px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <SectionLabel>
          <T context="heading for the card holding the case-only price file log columns">
            Price file log
          </T>
        </SectionLabel>
        {isEditing ? (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={updateMutation.isPending}
              onClick={() => {
                setState((prev) => ({
                  ...prev,
                  values: valuesFrom(prev.source),
                }));
                setIsEditing(false);
              }}
            >
              <T context="cancel editing the price file log fields">Cancel</T>
            </Button>
            <Button
              size="sm"
              disabled={updateMutation.isPending || !isDirty}
              onClick={() => void save()}
            >
              <T context="save the price file log field edits">Save Fields</T>
            </Button>
          </div>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setIsEditing(true)}
            aria-label={gt('Edit price file log fields', {
              context:
                'aria label for the pencil button on the price file log fields card',
            })}
          >
            <PencilSimpleIcon className="h-4 w-4" />
          </Button>
        )}
      </div>

      {isEditing ? (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <Label
              htmlFor="pricelist-case-vendor-code"
              className="text-xs text-muted-foreground"
            >
              <T context="label for the export's 3-letter vendor / commodity code on a price change">
                3 letter code / PG
              </T>
            </Label>
            <Input
              id="pricelist-case-vendor-code"
              value={state.values.vendorCode}
              maxLength={32}
              disabled={updateMutation.isPending}
              className="h-8 w-40 text-sm uppercase"
              placeholder={pricelistCase.companyCode ?? ''}
              onChange={(event) =>
                patch({ vendorCode: event.target.value.toUpperCase() })
              }
            />
            <span className="text-xs text-muted-foreground">
              {pricelistCase.companyCode ? (
                <T context="hint under the vendor code field naming the vendor's own code it defaults to">
                  Blank uses the vendor's code,{' '}
                  <Var>{pricelistCase.companyCode}</Var>. Override it for a
                  commodity file (wire, conduit) that spans many vendors.
                </T>
              ) : (
                <T context="hint under the vendor code field when the vendor has no code of its own">
                  This vendor has no code of its own; set one here for the
                  export.
                </T>
              )}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <Label
              htmlFor="pricelist-case-comparing-comments"
              className="text-xs text-muted-foreground"
            >
              <T context="label for the export's national IM comparing internal comments column">
                National IM comparing — internal comments
              </T>
            </Label>
            <Textarea
              id="pricelist-case-comparing-comments"
              rows={3}
              value={state.values.nationalImComparingComments}
              disabled={updateMutation.isPending}
              onChange={(event) =>
                patch({ nationalImComparingComments: event.target.value })
              }
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label
              htmlFor="pricelist-case-upload-comments"
              className="text-xs text-muted-foreground"
            >
              <T context="label for the export's upload comment internal comments column">
                Upload comment — internal comments
              </T>
            </Label>
            <Textarea
              id="pricelist-case-upload-comments"
              rows={3}
              value={state.values.uploadComments}
              disabled={updateMutation.isPending}
              onChange={(event) =>
                patch({ uploadComments: event.target.value })
              }
            />
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-x-10 gap-y-3">
          <Field
            label={
              <T context="label for the export's 3-letter vendor / commodity code on a price change">
                3 letter code / PG
              </T>
            }
          >
            {effectiveCode ?? '—'}
            {state.source.vendorCode === null && effectiveCode !== null && (
              <span className="ml-1 text-xs text-muted-foreground">
                <T context="suffix marking that the shown vendor code is the vendor's own default, not a per-case override">
                  (vendor default)
                </T>
              </span>
            )}
          </Field>
          <Field
            label={
              <T context="label for the export's national IM comparing internal comments column">
                National IM comparing — internal comments
              </T>
            }
          >
            <span className="whitespace-pre-wrap">
              {state.source.nationalImComparingComments ?? '—'}
            </span>
          </Field>
          <Field
            label={
              <T context="label for the export's upload comment internal comments column">
                Upload comment — internal comments
              </T>
            }
          >
            <span className="whitespace-pre-wrap">
              {state.source.uploadComments ?? '—'}
            </span>
          </Field>
        </div>
      )}
    </div>
  );
}
